import { Command } from 'cmdk';
import debounce from 'lodash.debounce';
import { useCallback, useMemo, useState } from 'react';
import { useFetcher } from 'react-router';
import { twMerge } from 'tailwind-merge';
import Badge from '~/components/Badge';
import Button from '~/components/button/Button';
import LinkButton from '~/components/button/LinkButton';
import Submit from '~/components/button/Submit';
import Card from '~/components/Card';
import Divide from '~/components/Divide';
import Form from '~/components/Form';
import Input, { InputPrimitive } from '~/components/form-controls/Input';
import { baseTextFieldStyles } from '~/components/form-controls/sharedFieldStyles';
import Icon from '~/components/Icon';
import Page from '~/components/layout/page/Page';
import LineItem from '~/components/LineItem';
import LoadingBoundary from '~/components/LoadingBoundary';
import PaginationControls from '~/components/pagination/PaginationControls';
import usePagination from '~/components/pagination/usePagination';
import Spinner from '~/components/Spinner';
import { FORM_KEY, SEARCH_PARAM, useLoaderData } from './selectProducts';

type ProductRegisterLoaderData = ReturnType<typeof useLoaderData>;
type Variant =
  ProductRegisterLoaderData['products'][number]['variants'][number];

function SelectVariantActions({
  variant,
  onRemove,
  index,
}: {
  variant: Variant;
  onRemove: (v: Variant) => void;
  index: number;
}) {
  return (
    <div className="flex items-end justify-end gap-6 sm:items-stretch">
      <input
        type="hidden"
        name={`${FORM_KEY}[${index}][id]`}
        value={variant.id}
        key={variant.id}
      />
      <Input
        type="number"
        label="Quantity"
        id={`${variant.id}-quantity`}
        name={`${FORM_KEY}[${index}][quantity]`}
        defaultValue={1}
        min={1}
        required
      />

      <Button
        // TODO: check how sizing lines up when new button implementation hits
        variant="outlined"
        className="sm:w-14 sm:flex-none"
        onClick={() => onRemove(variant)}
      >
        <Icon
          icon="Trash"
          title={`Remove ${variant.title}`}
          className="size-5"
        />
        <span className="ml-1 sm:sr-only">Remove</span>
      </Button>
    </div>
  );
}

function SuggestedVariants({
  suggestedVariants,
  possibleVariants,
  isAccepted,
  onAccept,
}: {
  suggestedVariants: Variant[];
  possibleVariants: Variant[];
  isAccepted: (variant: Variant) => boolean;
  onAccept: (variant: Variant) => void;
}) {
  const allVariants = useMemo(
    () => [
      ...suggestedVariants.map((v) => ({ ...v, type: 'suggested' })),
      ...possibleVariants.map((v) => ({ ...v, type: 'possible' })),
    ],
    [suggestedVariants, possibleVariants],
  );

  const [
    pageVariants,
    { currentPage, pageCount, next, hasNext, previous, hasPrevious },
  ] = usePagination({
    items: allVariants,
    itemsPerPage: 3,
  });

  if (!allVariants.length) return null;

  return (
    <Card title="Suggested Products">
      <div className="flex flex-row items-center justify-center gap-4">
        <Icon icon="SparklesSolid" className="size-6" />
        <p>
          We&apos;ve identified a few possible matches based on your submission.
          Please confirm your selection, or search below if you don&apos;t see
          the right product.
        </p>
      </div>

      <Divide as="ul" ariaLabel="Suggested Products">
        {pageVariants.map((variant) => {
          const accepted = isAccepted(variant);
          return (
            <li key={variant.id}>
              <LineItem
                name={variant.title}
                variant="small"
                imgSrc={variant.imgUrl}
                options={variant.options}
                footnote={
                  variant.type === 'suggested' ? (
                    <Badge variant="success">Best Match </Badge>
                  ) : (
                    <Badge variant="default"> Likely Match </Badge>
                  )
                }
              >
                <div className="flex items-center justify-end gap-3">
                  {accepted ? (
                    <span className="flex items-center gap-1 label-lg text-brand">
                      <Icon icon="CheckCircleSolid" className="size-4" />
                      Added To Registration
                    </span>
                  ) : (
                    <Button
                      variant="outlined"
                      className="flex gap-2"
                      onClick={() => onAccept(variant)}
                    >
                      Accept Suggestion
                    </Button>
                  )}
                </div>
              </LineItem>
            </li>
          );
        })}
      </Divide>
      <PaginationControls
        currentPage={currentPage}
        pageCount={pageCount}
        next={next}
        hasNext={hasNext}
        previous={previous}
        hasPrevious={hasPrevious}
        className="justify-center"
      />
    </Card>
  );
}
// TODO: consider moving search to Combobox
export default function SelectProductsPage() {
  const {
    identifyProductFormPlaceholder,
    identifyProductUrlButtonText,
    identifyProductDetailText,
    identifyProductUrl,
    suggestedVariants,
    possibleVariants,
  } = useLoaderData();

  const [selectedVariants, setSelectedVariants] = useState<Variant[]>([]);

  const [searchTerm, setSearchTerm] = useState('');

  const fetcher = useFetcher<ProductRegisterLoaderData>();

  const submitSearch = useMemo(
    () =>
      debounce((search: string) => {
        // oxlint-disable-next-line @typescript-eslint/no-floating-promises -- fetcher.submit is not expected to return a value; loader handles updates/errors
        fetcher.submit({ [SEARCH_PARAM]: search }, { method: 'GET' });
      }, 200),
    [fetcher],
  );

  const handleSearch = useCallback(
    (search: string) => {
      setSearchTerm(search);
      submitSearch(search);
    },
    [setSearchTerm, submitSearch],
  );

  const removeVariant = (variant: Variant) => {
    setSelectedVariants((prevSelectedVariants) =>
      prevSelectedVariants.filter((v) => v.id !== variant.id),
    );
  };

  const selectVariant = (variant: Variant) =>
    setSelectedVariants((prevSelectedVariants) =>
      prevSelectedVariants.some((v) => v.id === variant.id)
        ? prevSelectedVariants
        : [...prevSelectedVariants, variant],
    );

  const isVariantSelected = (variant: Variant) =>
    selectedVariants.some((v) => v.id === variant.id);

  return (
    <Page.Root title="Product Selection" hideHeader>
      <LoadingBoundary>
        <Page.Section title="Getting started">
          <span className="flex items-center gap-2 text-on-surface">
            {identifyProductDetailText}
          </span>
          {identifyProductUrl && (
            <LinkButton
              variant="outlined"
              to={identifyProductUrl}
              target="_blank"
              className="w-full md:w-1/2"
            >
              {identifyProductUrlButtonText}
            </LinkButton>
          )}
        </Page.Section>

        <SuggestedVariants
          suggestedVariants={suggestedVariants}
          possibleVariants={possibleVariants}
          isAccepted={isVariantSelected}
          onAccept={selectVariant}
        />

        <Page.Section title="Product Search">
          <Command shouldFilter={false}>
            <div className="relative">
              <Command.Input
                asChild
                placeholder={identifyProductFormPlaceholder}
                value={searchTerm}
                onValueChange={handleSearch}
              >
                <InputPrimitive
                  id="search"
                  className={twMerge(baseTextFieldStyles, 'py-2 pr-12')}
                  type="search"
                />
              </Command.Input>

              {fetcher.state === 'loading' && (
                <Spinner className="absolute top-3 right-3 size-6" />
              )}
            </div>

            <Command.List
              className={twMerge(
                'mt-4 max-h-96 overflow-auto',
                '[&>[cmdk-list-sizer]]:flex [&>[cmdk-list-sizer]]:flex-col [&>[cmdk-list-sizer]]:gap-4',
              )}
            >
              {fetcher.state === 'idle' &&
                fetcher.data?.products.length === 0 && (
                  <Command.Empty>
                    No products found, please try your search again.
                  </Command.Empty>
                )}

              {fetcher.data?.products.map((product) => (
                <Command.Group
                  key={product.id}
                  heading={product.title}
                  value={`${product.id}`}
                  className={twMerge(
                    'space-y-1 pb-1 subheading-md',
                    '[&>[cmdk-group-heading]]:mb-2 [&>[cmdk-group-heading]]:text-on-surface-variant',
                    '[&>[cmdk-group-items]]:flex [&>[cmdk-group-items]]:flex-col [&>[cmdk-group-items]]:gap-1 [&>[cmdk-group-items]]:px-2',
                  )}
                >
                  {product.variants.map((variant) => (
                    <Command.Item
                      key={variant.id}
                      value={`${variant.id}`}
                      onSelect={() => {
                        selectVariant(variant);
                        fetcher.data = undefined;
                      }}
                      className={twMerge(
                        'rounded-sm bg-surface-container-lowest px-3 py-1.5',
                        // hovered/highlighted
                        'aria-selected:bg-mix aria-selected:mix-with-on-surface/(--state-hovered-mix) aria-selected:mix-surface-container-lowest',
                      )}
                    >
                      <LineItem
                        name={`${variant.title}`}
                        variant="small"
                        imgSrc={variant.imgUrl ?? product.imgUrl}
                        options={variant.options}
                        key={variant.id}
                      />
                    </Command.Item>
                  ))}
                </Command.Group>
              ))}
            </Command.List>
          </Command>

          {fetcher.state === 'idle' && !!fetcher.data?.products.length && (
            <p>{fetcher.data.products.length} products found.</p>
          )}
        </Page.Section>

        {!!selectedVariants.length && (
          <Page.Section title="Review">
            <Form>
              <Divide as="ul" ariaLabel="Selected Products">
                {selectedVariants.map((variant, index) => (
                  <li key={variant.id}>
                    <LineItem
                      name={variant.title}
                      variant="small"
                      imgSrc={variant.imgUrl}
                      options={variant.options}
                    >
                      <SelectVariantActions
                        variant={variant}
                        index={index}
                        onRemove={removeVariant}
                      />
                    </LineItem>
                  </li>
                ))}
              </Divide>

              <Submit>Submit</Submit>
            </Form>
          </Page.Section>
        )}
      </LoadingBoundary>
    </Page.Root>
  );
}
